import { useLayoutEffect, useRef } from "react";

import { styled } from "styled-components";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes, rgba, setFontVariant } from "@similarweb/styles";
import {
    SearchInput as SearchInputComponent,
    SearchIconContainer,
    ClearSearchIconContainer,
} from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

const SearchWrapper = styled(Flex)`
    position: relative;
    margin: auto 0;

    .SearchInput-container {
        width: 176px;
        height: 32px;
        border-radius: 40px;
        margin: 0;

        ${SearchIconContainer} {
            top: 6px;
            left: 8px;
            width: 20px;

            & path {
                fill: ${colorsPalettes.carbon[300]};
            }
        }

        ${ClearSearchIconContainer} {
            top: 7px;
            right: 10px;
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: ${colorsPalettes.carbon[300]};
            transition: 0.2s;

            & svg {
                margin: 3px 0 0 3px;
                width: 12px;

                & path {
                    fill: ${colorsPalettes.carbon[25]};
                }
            }

            &:hover {
                background: ${colorsPalettes.carbon[400]};
                cursor: pointer;
            }
        }

        &:hover [data-automation-icon-name="search"] svg path {
            fill: ${colorsPalettes.carbon[400]};
        }

        .SearchInput {
            ${setFontVariant("input2", colorsPalettes.carbon[400])};
            background-color: ${rgba(colorsPalettes.carbon[200], 0.2)};
            height: 100%;
            width: 100%;
            outline: none;
            border: 1px solid transparent;
            box-sizing: border-box;
            padding: 5px 40px 5px 30px;
            box-shadow: none !important;
            border-radius: 40px;
            transition: 0.1s;

            &::placeholder {
                ${setFontVariant("input2", colorsPalettes.carbon[200])};
            }

            &:hover {
                background-color: ${rgba(colorsPalettes.carbon[500], 0.1)};
            }

            &:focus {
                border-color: ${colorsPalettes.carbon[300]};
            }
        }
    }
`;

interface IAlertsSearchInputProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    isDisabled?: boolean;
}

export const SearchInput = ({ value, onChange, placeholder, isDisabled }: IAlertsSearchInputProps) => {
    const { translate } = useTranslate();

    // Intentional: on unmount (tab switch) the ref becomes true, so on remount
    // SearchInputComponent's getDerivedStateFromProps clears the stale input value.
    const clearValueRef = useRef(false);

    useLayoutEffect(() => {
        clearValueRef.current = false;

        return () => {
            clearValueRef.current = true;
        };
    }, []);

    return (
        <SearchWrapper data-automation="alerts-search-input">
            <SearchInputComponent
                clearValue={clearValueRef.current}
                defaultValue={value}
                debounce={400}
                onChange={(newValue: string) => onChange(newValue.trim())}
                placeholder={placeholder ?? translate("activation.alerts.filters.search-placeholder")}
                disabled={isDisabled}
            />
        </SearchWrapper>
    );
};
